import React, { useEffect, useMemo, useState } from "react";
import axios from "../config/axiosInstance";
import { checkIfAuthenticated } from "../utils/utils";
import {
  HiOutlineMail,
  HiOutlineLockClosed,
  HiOutlineUser,
  HiEye,
  HiEyeOff,
  HiInformationCircle,
  HiCheck,
  HiPhone,
} from "react-icons/hi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import "./signup.css";
import { ADMIN } from "../config/apiConfig";

export default function SignupPage() {
  // Campos
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [phone, setPhone] = useState("");

  // Helpers de telefone BR
  const onlyDigits = (s = "") => s.replace(/\D/g, "");
  const maskPhoneBR = (value = "") => {
    const d = onlyDigits(value).slice(0, 11); // limita a 11
    if (d.length <= 10) {
      // (99) 9999-9999
      return d
        .replace(/^(\d{0,2})/, "($1")
        .replace(/^\((\d{2})/, "($1) ")
        .replace(/^\((\d{2})\)\s?(\d{0,4})/, "($1) $2")
        .replace(/^\((\d{2})\)\s?(\d{4})(\d{0,4})/, "($1) $2-$3")
        .trim();
    }
    // (99) 99999-9999
    return d
      .replace(/^(\d{0,2})/, "($1")
      .replace(/^\((\d{2})/, "($1) ")
      .replace(/^\((\d{2})\)\s?(\d{0,5})/, "($1) $2")
      .replace(/^\((\d{2})\)\s?(\d{5})(\d{0,4})/, "($1) $2-$3")
      .trim();
  };
  const isValidPhoneBR = (value = "") => {
    const d = onlyDigits(value);
    // válido se 10 (fixo) ou 11 (celular). Se 11, o dígito 3 deve ser 9.
    if (d.length === 10) return true;
    if (d.length === 11) return d[2] === "9";
    return false;
  };

  // UI
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (checkIfAuthenticated()) window.location.replace("/dashboard");
  }, []);

  const passScore = useMemo(() => {
    let s = 0;
    if (pass.length >= 8) s++;
    if (/[A-Z]/.test(pass)) s++;
    if (/[a-z]/.test(pass)) s++;
    if (/\d/.test(pass)) s++;
    if (/[^A-Za-z0-9]/.test(pass)) s++;
    return Math.min(s, 5);
  }, [pass]);

  const validPhone = useMemo(() => isValidPhoneBR(phone), [phone]);

  const samePass = pass && pass2 && pass === pass2;
  const canSubmit = useMemo(() => {
    if (loading) return false;
    return (
      name.trim().length >= 2 &&
      email.trim().length > 3 &&
      passScore >= 3 &&
      samePass &&
      validPhone &&
      agree
    );
  }, [name, email, passScore, samePass, agree, validPhone, loading]);

  const handleCaps = (e) =>
    setCapsOn(!!(e.getModifierState && e.getModifierState("CapsLock")));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      setErr(null);
      setOk(false);
      const res = await axios.post(ADMIN.NEW_CUSTOMER, {
        name: name.trim(),
        email: email.trim(),
        password: pass,
        agreeToTerms: !!agree,
        phoneNumber: phone,
      });
      if (res?.data?.status === "success" || res?.data?.ok) {
        setOk(true);
        setTimeout(() => window.location.replace("/login"), 1200);
      } else {
        throw new Error(
          res?.data?.message || "Não foi possível concluir o cadastro."
        );
      }
    } catch (e2) {
      setErr(
        e2?.response?.data?.message ||
          e2?.message ||
          "Não foi possível concluir o cadastro."
      );
    } finally {
      setLoading(false);
    }
  };

  const strengthLabels = ["Muito fraca", "Fraca", "Ok", "Boa", "Excelente"];

  return (
    <div className="sgc">
      {/* Decor minimalista */}
      <div className="sgc__blob sgc__blob--1" />
      <div className="sgc__blob sgc__blob--2" />
      <div className="sgc__noise" aria-hidden />

      <header className="sgc__header">
        <a className="sgc__brand" href="/">
          <div className="sgc__logo">R</div>
          <span>
            Relinq<span style={{ color: "var(--primary)" }}>Sales</span>.
          </span>
        </a>
        <nav className="sgc__nav">
          <a href="/login" className="sgc__navlink shadow-md">
            Entrar
          </a>
        </nav>
      </header>

      <main className="sgc__main">
        {/* Lateral com claims */}
        <aside className="sgc__aside sgc__aside--enhanced hidden md:block">
          {/* badge sutil */}
          <div className="sgc__badge reveal" style={{ "--d": "0ms" }}>
            <span className="dot"></span> Comece em 60 segundos
          </div>

          <h1 className="reveal" style={{ "--d": "80ms" }}>
            Simples. <span className="accent">Rápido.</span> Focado em{" "}
            <span className="accent-underline">resultado</span>.
          </h1>

          <p className="reveal" style={{ "--d": "160ms" }}>
            Organize seu pipeline, foque nos próximos passos e tenha
            previsibilidade — sem fricção.
          </p>

          {/* métricas rápidas */}
          <div className="sgc__chips reveal" style={{ "--d": "220ms" }}>
            <div className="chip">
              <span className="chip__dot" />
              +27% conversão
            </div>
            <div className="chip">
              <span className="chip__dot" />
              Forecast assertivo
            </div>
            <div className="chip">
              <span className="chip__dot" />
              Menos follow-ups perdidos
            </div>
          </div>

          {/* features com ícone / hover */}
          <ul className="sgc__features">
            <li className="feature reveal" style={{ "--d": "280ms" }}>
              <div className="feature__icon">✅</div>
              <div className="feature__text">
                <div className="feature__title">Playbooks claros</div>
                <div className="feature__desc">
                  Padronize o que funciona para todo o time.
                </div>
              </div>
            </li>
            <li className="feature reveal" style={{ "--d": "340ms" }}>
              <div className="feature__icon">⏰</div>
              <div className="feature__text">
                <div className="feature__title">Follow-ups na hora</div>
                <div className="feature__desc">
                  Sugestões inteligentes para não deixar esfriar.
                </div>
              </div>
            </li>
            <li className="feature reveal" style={{ "--d": "400ms" }}>
              <div className="feature__icon">📊</div>
              <div className="feature__text">
                <div className="feature__title">Indicadores acionáveis</div>
                <div className="feature__desc">
                  Entenda gargalos e acelere a receita.
                </div>
              </div>
            </li>
          </ul>
        </aside>

        {/* Card do formulário */}
        <section className="sgc__card">
          <div className="sgc__card__head">
            <h2>Crie sua conta</h2>
            <p>Leva menos de 1 minuto.</p>
          </div>

          <form className="sgc__form" onSubmit={submit} noValidate>
            {/* Nome */}
            <div className="field">
              <HiOutlineUser className="field__icon" />
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder=" "
              />
              <label htmlFor="name">Nome completo</label>
            </div>

            {/* E-mail */}
            <div className="field">
              <HiOutlineMail className="field__icon" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder=" "
                autoComplete="username"
              />
              <label htmlFor="email">E-mail</label>
            </div>

            {/* Telefone */}
            <div className={`field ${phone && !validPhone ? "is-error" : ""}`}>
              <HiPhone className="field__icon" />
              <input
                id="phone"
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(maskPhoneBR(e.target.value))}
                placeholder=" "
                autoComplete="tel"
                aria-invalid={phone && !validPhone ? "true" : "false"}
              />
              <label htmlFor="phone">Telefone (com DDD)</label>
            </div>
            {phone && !validPhone && (
              <div className="hint warn">
                <HiInformationCircle />
                Telefone inválido. Use DDD número (ex.: (11) 98888-7777)
              </div>
            )}

            {/* Senha */}
            <div className="field">
              <HiOutlineLockClosed className="field__icon" />
              <input
                id="password"
                type={showPass ? "text" : "password"}
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                onKeyUp={handleCaps}
                onKeyDown={handleCaps}
                required
                minLength={8}
                placeholder=" "
                autoComplete="new-password"
              />
              <label htmlFor="password">Senha</label>
              <button
                type="button"
                className="field__toggle"
                onClick={() => setShowPass((v) => !v)}
                aria-label={showPass ? "Ocultar senha" : "Mostrar senha"}
                title={showPass ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPass ? <HiEyeOff /> : <HiEye />}
              </button>
            </div>

            {/* Hint / Caps */}
            <div className="hintrow">
              <span className={`hint ${capsOn ? "warn" : ""}`}>
                <HiInformationCircle />
                {capsOn ? "Caps Lock ativo" : "mín. 8 caracteres"}
              </span>
            </div>

            {/* Força da senha */}
            <div className="strength">
              {[1, 2, 3, 4, 5].map((i) => (
                <span
                  key={i}
                  className={`pill ${passScore >= i ? "on" : ""}`}
                />
              ))}
              <span className="strength__label">
                {pass ? strengthLabels[Math.max(0, passScore - 1)] : "—"}
              </span>
            </div>

            {/* Confirmar */}
            <div className={`field ${pass2 && !samePass ? "is-error" : ""}`}>
              <HiOutlineLockClosed className="field__icon" />
              <input
                id="password2"
                type={showPass2 ? "text" : "password"}
                value={pass2}
                onChange={(e) => setPass2(e.target.value)}
                required
                minLength={8}
                placeholder=" "
                autoComplete="new-password"
              />
              <label htmlFor="password2">Confirmar senha</label>
              <button
                type="button"
                className="field__toggle"
                onClick={() => setShowPass2((v) => !v)}
                aria-label={showPass2 ? "Ocultar senha" : "Mostrar senha"}
                title={showPass2 ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPass2 ? <HiEyeOff /> : <HiEye />}
              </button>
            </div>
            {pass2 && !samePass && (
              <div className="hint warn">
                <HiInformationCircle />
                As senhas não coincidem
              </div>
            )}

            {/* Termos */}
            <label className="check">
              <input
                type="checkbox"
                checked={agree}
                onChange={() => setAgree((v) => !v)}
              />
              <span>
                Concordo com os{" "}
                <a
                  href="https://relinqsales.com.br/termos"
                  target="_blank"
                  rel="noreferrer"
                >
                  Termos
                </a>{" "}
                e{" "}
                <a
                  href="https://relinqsales.com.br/politica-privacidade"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacidade
                </a>
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              className={`cta ${!canSubmit ? "is-disabled" : ""}`}
              disabled={!canSubmit}
            >
              {loading ? (
                <>
                  <AiOutlineLoading3Quarters className="spin" />
                  Criando…
                </>
              ) : (
                "Criar conta"
              )}
            </button>

            {err && <div className="alert error">{err}</div>}
            {ok && (
              <div className="alert ok">Conta criada! Redirecionando…</div>
            )}

            <div className="foot flex justify-center">
              Já tem conta? <a href="/login">Entrar</a>
            </div>
          </form>
        </section>
      </main>

      <footer className="sgc__footer">
        © {new Date().getFullYear()} Relinq Sales
      </footer>
    </div>
  );
}
